import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from 'react-toastify';
import { apiFetch } from "../../api/api";
import {
  getDeviceDetail,
  setDeviceActivation,
  unclaimDevice,
  testPrint,
  setDeviceOrg,
} from "./printManager.device.api";
import { useOrg } from "../orgs/org-context";

const DeviceDetail = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();

  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [changingActivation, setChangingActivation] = useState(false);

  const [sendingPrinterId, setSendingPrinterId] = useState(null);
  const [printerDiagnostics, setPrinterDiagnostics] = useState({});
  const { activeOrg } = useOrg();
  const [orgOpen, setOrgOpen] = useState(false);
  const [changingOrg, setChangingOrg] = useState(false);
  const prevOrgRef = useRef(null);

  // -----------------------------
  // LOAD DEVICE
  // -----------------------------
  const loadDevice = async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      else setRefreshing(true);

      const data = await getDeviceDetail(deviceId);
      console.log("data:", data)
      // 🔥 KEY: if no device returned → redirect
      if (!data) {
        navigate("/devices");
        return;
      }

      setDevice(data);

      if (data?.printers?.length) {
        loadPrinterDiagnostics(data.printers);
      }

    } catch (err) {
      console.error("❌ loadDevice failed", err);

      // 🔥 KEY: if 404 → redirect
      if (err.message === "Not Found" || err.status === 404) {
        navigate("/devices");
        return;
      }

    } finally {
      if (isInitial) setLoading(false);
      else setRefreshing(false);
    }
  };


    useEffect(() => {
      if (!prevOrgRef.current && activeOrg) {
        prevOrgRef.current = activeOrg;
      }
    }, [activeOrg]);


    useEffect(() => {
      if (!prevOrgRef.current || !activeOrg) return;

      if (activeOrg._id === prevOrgRef.current._id) return;

      prevOrgRef.current = activeOrg;

      navigate("/devices");
    }, [activeOrg, navigate]);

  useEffect(() => {
    loadDevice(true);

    const interval = setInterval(() => loadDevice(false), 8000);
    return () => clearInterval(interval);
  }, [deviceId]);

  // -----------------------------
  // LOAD PRINTER DIAGNOSTICS
  // -----------------------------
  const loadPrinterDiagnostics = async (printers) => {
    try {
      const results = {};

      await Promise.all(
        printers.map(async (p) => {
          try {
            const res = await apiFetch(
              `/api/printmanager/printers/${encodeURIComponent(
                p.printerId
              )}/diagnostics`
            );

            results[p.printerId] = res;
          } catch {
            // ignore per-printer failure
          }
        })
      );

      setPrinterDiagnostics(results);
    } catch (err) {
      console.error("❌ diagnostics load failed", err);
    }
  };

  // -----------------------------
  // ACTIONS
  // -----------------------------
  const handleActivation = async () => {
    const isActive = !device.isActive;
    const action = isActive ? "Activate" : "Deactivate";
    if (!window.confirm(`${action} this Print Manager? ${isActive ? "It will be permitted to receive print jobs." : "It will stop receiving print jobs."}`)) return;
    setChangingActivation(true);
    try {
      const updated = await setDeviceActivation(deviceId, isActive);
      setDevice(current => ({ ...current, isActive: updated.isActive }));
      await loadDevice(false);
    } catch {
      window.alert(`Unable to ${action.toLowerCase()} this Print Manager.`);
    } finally {
      setChangingActivation(false);
    }
  };

  const handleRetire = async () => {
    if (!window.confirm("Retire this Print Manager? History stays; the installation loses access. Use Replace to move PCs.")) return;
    try { await apiFetch("/api/printmanager/pairing/retire", {method:"POST",body:{deviceId}}); navigate("/devices"); }
    catch { window.alert("Unable to retire this Print Manager."); }
  };

  const handleTestPrint = async (printerId) => {
    try {
      setSendingPrinterId(printerId);

      await testPrint(device.deviceId, printerId);

      alert(`Test sent to ${printerId}`);
    } catch (err) {
      alert("Test failed");
    } finally {
      setSendingPrinterId(null);
    }
  };

  // -----------------------------
  // HELPERS
  // -----------------------------
  const formatLastSeen = (seconds) => {
    if (seconds == null) return "—";

    if (seconds < 15) return "just now";
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;

    return `${Math.floor(seconds / 86400)}d ago`;
  };


  const formatLastJob = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleTimeString();
    } catch {
      return "—";
    }
  };

  // -----------------------------
  // LOADING
  // -----------------------------
  if (loading) return <div style={{ padding: 20 }}>Loading...</div>;
  if (!device) {
    toast.error("Device not available");
    navigate("/devices");
    return null;
  }

  // -----------------------------
  // RENDER
  // -----------------------------
  return (
    <div style={{ padding: 20 }}>
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>
          Device Detail{" "}
          {refreshing && (
            <span style={{ fontSize: 12, marginLeft: 10 }}>
              Refreshing...
            </span>
          )}
        </h2>

        <button onClick={() => navigate("/devices")}>
          ← Back
        </button>
      </div>

      <div className="device-page">
        <div className="device-layout">

          {/* LEFT COLUMN */}
          <div className="device-sidebar">
            <h3>Device Info</h3>

            <p><b>Name:</b> {device.deviceName}</p>
            <p><b>ID:</b> {device.deviceId}</p>
            <p><b>Org:</b> {device.orgName || "—"}</p>
            <p><b>Status:</b> {device.status}</p>
            <p><b>Activation:</b> {device.retiredAt ? "Retired" : device.isActive === true ? "Active" : device.isActive === false ? "Inactive" : "Unknown"}</p>
            <p><b>Last Seen:</b> {formatLastSeen(device.lastSeenSecondsAgo)}</p>
            <p><b>Printers:</b> {device.printerCount || 0}</p>
            <p><b>Queue:</b> {device.queueDepth || 0}</p>

            <div className="device-section">
              <h3>Actions</h3>

              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

                <button onClick={() => loadDevice(false)}>
                  Refresh
                </button>

                <button onClick={handleRetire}>Retire</button>
                {!device.retiredAt && typeof device.isActive === "boolean" && (
                  <button onClick={handleActivation} disabled={changingActivation}>
                    {device.isActive ? "Deactivate Print Manager" : "Activate Print Manager"}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="device-main">
            <div className="device-section">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <h3 style={{ margin: 0 }}>
                  Printers ({device.printerCount || 0})
                </h3>

                <strong>
                  Queue: {device.queueDepth || 0}
                </strong>
              </div>

              {/* HEADER */}
              <div className="pm-row pm-header-row">
                <div>Name</div>
                <div>Status</div>
                <div>Online</div>
                <div>Queue</div>
                <div>Last Job</div>
                <div>Job</div>
                <div></div>
              </div>

              {/* EMPTY */}
              {(!device.printers || device.printers.length === 0) && (
                <div className="pm-empty">No printers detected</div>
              )}

              {/* ROWS */}
              {device.printers?.map((p, i) => {
                const diag = printerDiagnostics[p.printerId];

                return (
                  <div key={i} className="pm-row">
                    <div>{p.displayName || p.printerId}</div>
                    <div>{p.status || "—"}</div>

                    <div>
                      <span className={p.isOnline ? "status-online" : "status-offline"}>
                        {p.isOnline ? "Yes" : "No"}
                      </span>
                    </div>

                    <div>{p.queueDepth || 0}</div>

                    <div>{formatLastJob(p.lastJobAt)}</div>

                    {/* JOB STATUS */}
                    <div>
                      {diag?.status || "—"}
                    </div>

                    {/* TEST BUTTON */}
                    <div>
                      <button
                        disabled={!p.isOnline || sendingPrinterId === p.printerId}
                        onClick={() => handleTestPrint(p.printerId)}
                      >
                        {sendingPrinterId === p.printerId
                          ? "Sending..."
                          : "Test"}
                      </button>

                      {diag?.status === "failed" && (
                        <button
                          style={{ marginLeft: 5 }}
                          onClick={() => handleTestPrint(p.printerId)}
                        >
                          Retry
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DeviceDetail;
